import {Link, Stack} from "@fluentui/react";
import {FC} from "react";
import {NavLink, useNavigate} from "react-router-dom";
import {
    AdminUsersTenantReturnDto,
    ApplicationUser,
    Company,
    CompanySettings,
    CompanySettingsReturnDto,
    Contact,
    Department,
    EmailTestingRequest,
    Item,
    ItemGroup,
    ItemReturnDto,
    ItemReturnItemGroupItemItemGroupDto,
    Project,
    ProjectReturnDto,
    Subject,
    SubjectReturnDto,
    SupportTicket,
    THeMove,
    THeOrder,
    WikiEntry,
} from "../API";
import {DocumentType, UserQuery} from "../graphql-types";
import {AsAcKey} from "../helpers/AsAcKey";
import {AppRoute} from "../AppRoutes";
import {supportTicketTitle} from "../helpers/SupportTicketTitle";

interface IMakeLink {
    title: (string | undefined)[];
    params: any[];
    route: (...params: any[]) => string;
}

const MakeLink: FC<IMakeLink> = ({params, route, title}) => {
    const history = useNavigate();

    return (
        <NavLink data-selection-disabled={true} to={route(...params)}>
            <Link>
                <Stack>
                    {title.map((m) => (
                        <span key={m} className="has-clickable-action">
              {m}
            </span>
                    ))}
                </Stack>
            </Link>
        </NavLink>
    );
};

interface IOpen<T> {
    resource: T;
}

interface IOpenWithUserInfo<T> extends IOpen<T> {
    userInfo?: UserQuery;
}

interface IOpenWithUserInfoAndParent<P, T> extends IOpenWithUserInfo<T> {
    parent: P;
}

export const OpenItemGroup: FC<
    IOpen<ItemGroup | ItemReturnItemGroupItemItemGroupDto>
> = ({resource}) => (
    <MakeLink
        title={[resource?.name]}
        params={[resource?.id]}
        route={AppRoute.ITEM_GROUP_OVERVIEW}
    />
);
export const OpenDepartment: FC<IOpen<Department>> = ({resource}) => (
    <MakeLink
        title={[resource?.name]}
        params={[resource?.id]}
        route={AppRoute.DEPARTMENT_OVERVIEW}
    />
);
export const OpenCompany: FC<IOpen<Company>> = ({resource}) => (
    <MakeLink
        title={[resource?.name]}
        params={[resource?.id]}
        route={AppRoute.COMPANY_OVERVIEW}
    />
);
export const OpenContact: FC<IOpen<Contact>> = ({resource}) => (
    <MakeLink
        title={[resource?.fullName]}
        params={[resource?.id]}
        route={AppRoute.CONTACT_OVERVIEW}
    />
);
export const OpenProject: FC<IOpen<Project | ProjectReturnDto>> = ({
                                                                       resource,
                                                                   }) => (
    <MakeLink
        title={[resource?.name]}
        params={[resource?.id]}
        route={AppRoute.PROJECT_OVERVIEW}
    />
);
export const OpenCustomer: FC<IOpen<Subject | SubjectReturnDto>> = ({
                                                                        resource,
                                                                    }) => (
    <MakeLink
        title={[resource?.shortName, resource?.longName]}
        params={[resource?.id]}
        route={AppRoute.CUSTOMER_OVERVIEW}
    />
);
export const OpenItem: FC<IOpen<Item | ItemReturnDto>> = ({resource}) => (
    <MakeLink
        title={[resource?.name]}
        params={[resource?.id]}
        route={AppRoute.ITEM_OVERVIEW}
    />
);
export const OpenCompanySettings: FC<
    IOpen<CompanySettings | CompanySettingsReturnDto>
> = ({resource}) => (
    <MakeLink
        title={[resource?.pantheonCompanyName ?? resource?.connectionName]}
        params={[resource?.id]}
        route={AppRoute.EDIT_COMPANY}
    />
);
export const OpenWebDocumentTHeOrder: FC<
    IOpenWithUserInfoAndParent<DocumentType, THeOrder>
> = ({resource, userInfo, parent}) => (
    <MakeLink
        title={[AsAcKey(userInfo, resource?.acKey)]}
        params={[parent.id, resource?.acKey]}
        route={AppRoute.WEB_DOCUMENT_NEW_ORDER}
    />
);
export const OpenWebDocumentTHeMove: FC<
    IOpenWithUserInfoAndParent<DocumentType, THeMove>
> = ({resource, userInfo, parent}) => (
    <MakeLink
        title={[AsAcKey(userInfo, resource?.acKey)]}
        params={[parent.id, resource?.acKey]}
        route={AppRoute.WEB_DOCUMENT_NEW_MOVE}
    />
);
export const OpenSupportTicket: FC<IOpen<SupportTicket>> = ({resource}) => (
    <MakeLink
        title={[supportTicketTitle(resource?.title)]}
        params={[resource?.id]}
        route={AppRoute.EDIT_SUPPORT_TICKET}
    />
);
export const OpenUser: FC<IOpen<ApplicationUser>> = ({resource}) => (
    <MakeLink
        title={[resource?.userName, resource?.fullName]}
        params={[resource?.id]}
        route={AppRoute.ADMIN_EDIT_USER}
    />
);

interface IOpenUserById {
    id?: string;
    userName?: string;
    fullName?: string;
}

export const OpenUserById: FC<IOpenUserById> = ({id, userName, fullName}) => {
    if (!id) return <span/>;

    return (
        <MakeLink
            title={[userName ?? id, fullName]}
            params={[id]}
            route={AppRoute.ADMIN_EDIT_USER}
        />
    );
};
export const OpenAdminUser: FC<IOpen<AdminUsersTenantReturnDto>> = ({
                                                                        resource,
                                                                    }) => (
    <MakeLink
        title={[resource?.userName, `${resource?.firstName} ${resource?.lastName}`]}
        params={[resource?.id]}
        route={AppRoute.ADMIN_EDIT_USER}
    />
);
export const OpenEmailSettingsTesting: FC<IOpen<EmailTestingRequest>> = ({
                                                                             resource,
                                                                         }) => (
    <MakeLink
        title={[resource?.host]}
        params={[resource?.id]}
        route={AppRoute.EMAIL_SETTINGS_TESTING_OVERVIEW}
    />
);
export const OpenWikiEntry: FC<IOpen<WikiEntry>> = ({resource}) => (
    <MakeLink
        title={[resource?.title]}
        params={[resource?.id]}
        route={AppRoute.WIKI_ENTRY_OVERVIEW}
    />
);
